import type { LinhaDatasFatura } from '../../types';
import { formatarDocumento } from './termoNegociacao.ts';

export { formatarDocumento };

// Regras do relatório "Datas das faturas" (Gestão), sem DOM: a tela e o Excel usam as
// mesmas funções, e os testes rodam sob `node --test`.

export type CampoData =
  | 'leitura_anterior' | 'leitura_atual' | 'proxima_leitura'
  | 'apresentacao' | 'vencimento' | 'processamento';

/** As 6 datas na ordem em que aparecem na fatura. */
export const CAMPOS_DATA: { chave: CampoData; rotulo: string; curto: string }[] = [
  { chave: 'leitura_anterior', rotulo: 'Leitura anterior', curto: 'Leit. anterior' },
  { chave: 'leitura_atual', rotulo: 'Leitura atual', curto: 'Leit. atual' },
  { chave: 'proxima_leitura', rotulo: 'Próxima leitura', curto: 'Próx. leitura' },
  { chave: 'apresentacao', rotulo: 'Apresentação', curto: 'Apresentação' },
  { chave: 'vencimento', rotulo: 'Vencimento', curto: 'Vencimento' },
  { chave: 'processamento', rotulo: 'Processamento', curto: 'Processamento' },
];

/** Por que a data de processamento está (ou não) preenchida. */
export type SituacaoProcessamento =
  | 'ok' | 'debito' | 'espelho' | 'arrecadacao' | 'sem_pdf' | 'nao_lido' | 'leitura_antiga' | 'nao_encontrada';

export const SITUACAO_INFO: Record<SituacaoProcessamento, { rotulo: string; explicacao: string }> = {
  ok: { rotulo: 'Encontrada', explicacao: 'Data lida no boleto da fatura.' },
  debito: { rotulo: 'Débito automático', explicacao: 'Fatura em débito automático não tem boleto, então não tem data de processamento.' },
  espelho: { rotulo: 'Espelho da fatura', explicacao: 'O espelho é a prévia da Equatorial e vem sem boleto.' },
  arrecadacao: { rotulo: 'Guia de arrecadação', explicacao: 'O boleto é guia de arrecadação (código começa com 8): esse modelo só traz a data de emissão, não a de processamento.' },
  sem_pdf: { rotulo: 'Sem PDF', explicacao: 'A fatura não tem o PDF da Equatorial salvo no sistema.' },
  nao_lido: { rotulo: 'PDF não lido', explicacao: 'O PDF existe, mas ainda não passou pelo extrator.' },
  leitura_antiga: { rotulo: 'Leitura antiga', explicacao: 'O PDF foi lido antes de o sistema buscar essa data.' },
  nao_encontrada: { rotulo: 'Não encontrada', explicacao: 'O extrator leu o PDF e não achou a data no boleto.' },
};

/** A ordem importa: o motivo "de negócio" (sem boleto) vem antes do motivo técnico. */
export function situacaoProcessamento(l: LinhaDatasFatura): SituacaoProcessamento {
  if (l.processamento) return 'ok';
  if (l.debito_automatico) return 'debito';
  if (l.espelho) return 'espelho';
  if (l.arrecadacao) return 'arrecadacao';
  if (!l.tem_extracao) return l.tem_pdf ? 'nao_lido' : 'sem_pdf';
  if (!l.processamento_lido) return 'leitura_antiga';
  return 'nao_encontrada';
}

/** Rótulos das datas que estão vazias. */
export function datasFaltando(l: LinhaDatasFatura): string[] {
  return CAMPOS_DATA.filter((c) => !l[c.chave]).map((c) => c.rotulo);
}

/** Só as datas do ciclo de leitura que estão vazias (o processamento tem situação própria). */
export function leiturasFaltando(l: LinhaDatasFatura): string[] {
  return CAMPOS_DATA.filter((c) => c.chave !== 'processamento' && !l[c.chave]).map((c) => c.rotulo);
}

/** As 5 datas do ciclo de leitura (tudo menos o processamento, que depende do boleto). */
export function leiturasCompletas(l: LinhaDatasFatura): boolean {
  return leiturasFaltando(l).length === 0;
}

const MESES = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];

/** '2026-06' → 'JUN/2026' */
export function rotuloMes(mesIso: string): string {
  const [ano, mes] = mesIso.split('-').map(Number);
  return MESES[mes - 1] ? `${MESES[mes - 1]}/${ano}` : mesIso;
}

/** '2026-06-30' → '30/06/2026' (sem passar por Date: evita o −1 dia do fuso). */
export function dataBr(iso: string | null | undefined): string {
  const m = (iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
}

const soDigitos = (s: string | null | undefined) => (s || '').replace(/\D/g, '');
const semAcento = (s: string | null | undefined) =>
  (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const semZeroEsquerda = (s: string | null | undefined) => soDigitos(s).replace(/^0+/, '');

const RE_NUMERO_NOVO = /^\d{5,10}012\d{2}$/;
const RE_NUMERO_ANTIGO_100 = /^100\d{8}$/;

/**
 * Número novo da Equatorial: termina em 012 + 2 dígitos ('406018901201'). O antigo de 11
 * dígitos começa com 100 e às vezes também termina assim por acaso. Mesma regra da view
 * vw_datas_faturas — mudar uma é mudar a outra.
 */
export function ehNumeroNovo(uc: string | null | undefined): boolean {
  const n = semZeroEsquerda(uc);
  return RE_NUMERO_NOVO.test(n) && !RE_NUMERO_ANTIGO_100.test(n);
}

/** O que as regras de UC e de busca leem da linha: serve à fatura e ao boleto (datasBoletos.ts). */
export type LinhaComUc = Pick<LinhaDatasFatura, 'uc' | 'uc_id' | 'uc_nova' | 'uc_antiga' | 'cliente' | 'cpf_cnpj'>;

/**
 * Os dois números da UC da linha (só dígitos). A RPC já manda os dois a partir do
 * cadastro; sem eles, o número com que a fatura foi gravada vai para a coluna do formato.
 */
export function numerosUc(l: LinhaComUc): { nova: string; antiga: string } {
  if (l.uc_nova || l.uc_antiga) return { nova: l.uc_nova || '', antiga: l.uc_antiga || '' };
  const uc = semZeroEsquerda(l.uc);
  return ehNumeroNovo(uc) ? { nova: uc, antiga: '' } : { nova: '', antiga: uc };
}

/** Chave da unidade: o cadastro quando existe, senão o número da fatura. */
export function chaveUc(l: LinhaComUc): string {
  return l.uc_id != null ? `id:${l.uc_id}` : `uc:${semZeroEsquerda(l.uc)}`;
}

export type FiltroEmpresa = 'todas' | 'vigor' | 'parceiras';
export type FiltroSituacao = 'todas' | 'faltando' | 'sem_processamento' | 'completas';

export interface FiltrosDatas {
  busca?: string;
  empresa?: FiltroEmpresa;
  situacao?: FiltroSituacao;
  /** '2026-01' (inclusive) */
  mesDe?: string;
  /** '2026-09' (inclusive) */
  mesAte?: string;
}

/**
 * Busca por UC — qualquer um dos dois números — ou CPF/CNPJ (só dígitos, ignora pontuação e
 * zeros à esquerda da UC) ou nome.
 */
export function casaBusca(l: LinhaComUc, busca: string): boolean {
  const termo = busca.trim();
  if (!termo) return true;
  const dig = soDigitos(termo);
  if (dig.length >= 4 && /^[\d.\-/\s]+$/.test(termo)) {
    const alvo = dig.replace(/^0+/, '');
    return [l.uc, l.uc_nova, l.uc_antiga].some((uc) => semZeroEsquerda(uc).includes(alvo))
      || soDigitos(l.cpf_cnpj).includes(dig);
  }
  return semAcento(l.cliente).includes(semAcento(termo));
}

export function filtrarLinhas(linhas: LinhaDatasFatura[], f: FiltrosDatas): LinhaDatasFatura[] {
  return linhas.filter((l) => {
    if (f.mesDe && l.mes_iso < f.mesDe) return false;
    if (f.mesAte && l.mes_iso > f.mesAte) return false;
    if (f.empresa === 'vigor' && l.empresa) return false;
    if (f.empresa === 'parceiras' && !l.empresa) return false;
    if (f.situacao === 'faltando' && datasFaltando(l).length === 0) return false;
    if (f.situacao === 'sem_processamento' && l.processamento) return false;
    if (f.situacao === 'completas' && datasFaltando(l).length > 0) return false;
    return f.busca ? casaBusca(l, f.busca) : true;
  });
}

export interface CoberturaMes {
  mesIso: string;
  mes: string;
  total: number;
  comProcessamento: number;
  leiturasCompletas: number;
}

/** Quantas faturas o mês tem e quantas têm cada grupo de datas — base do gráfico de cobertura. */
export function coberturaPorMes(linhas: LinhaDatasFatura[]): CoberturaMes[] {
  const mapa = new Map<string, CoberturaMes>();
  for (const l of linhas) {
    let c = mapa.get(l.mes_iso);
    if (!c) {
      c = { mesIso: l.mes_iso, mes: rotuloMes(l.mes_iso), total: 0, comProcessamento: 0, leiturasCompletas: 0 };
      mapa.set(l.mes_iso, c);
    }
    c.total++;
    if (l.processamento) c.comProcessamento++;
    if (leiturasCompletas(l)) c.leiturasCompletas++;
  }
  return [...mapa.values()].sort((a, b) => a.mesIso.localeCompare(b.mesIso));
}

export interface ResumoDatas {
  faturas: number;
  ucs: number;
  meses: number;
  comProcessamento: number;
  leiturasCompletas: number;
  porSituacao: Record<SituacaoProcessamento, number>;
}

export function resumir(linhas: LinhaDatasFatura[]): ResumoDatas {
  const ucs = new Set<string>();
  const meses = new Set<string>();
  const porSituacao = Object.fromEntries(
    (Object.keys(SITUACAO_INFO) as SituacaoProcessamento[]).map((s) => [s, 0]),
  ) as Record<SituacaoProcessamento, number>;
  let comProcessamento = 0;
  let completas = 0;
  for (const l of linhas) {
    ucs.add(chaveUc(l));
    meses.add(l.mes_iso);
    if (l.processamento) comProcessamento++;
    if (leiturasCompletas(l)) completas++;
    porSituacao[situacaoProcessamento(l)]++;
  }
  return { faturas: linhas.length, ucs: ucs.size, meses: meses.size, comProcessamento, leiturasCompletas: completas, porSituacao };
}

/** Fração 0..1 sem dividir por zero. */
export const fracao = (parte: number, total: number) => (total > 0 ? parte / total : 0);
